import { useEffect, useState } from 'react';
import type { ErpConnectionView, ErpDocument, ErpDocumentAction, ErpDocumentKind, ErpDocumentPreview } from '../../api';
import { erpApi } from '../../api';
import { useResource } from '../../hooks';
import { LevelBadge, PanelCard, QuietEmpty, SectionHeader } from '../project/ui';
import { ActionMenu, Dialog, Field, FilterChips, Input, Pager, Select } from '../primitives';
import type { MenuAction } from '../primitives';
import { Fold, ResponsiveTable } from '../primitives/ResponsiveTable';
import type { Column } from '../primitives/ResponsiveTable';
import { ConfirmDialog, ErrorNotice, LoadingRows } from '../ui';
import { Timestamp } from '../status/StatusPieces';
import { ErpDocumentReadiness } from './ErpDocumentReadiness';
import { ErpDocumentSettingsForm } from './ErpDocumentSettingsForm';
import {
  attemptResolutionLabel,
  documentActionDone,
  documentActionLabel,
  documentActionName,
  documentActions,
  documentFilterFor,
  documentFilters,
  documentKindLabel,
  documentModeLabel,
  documentNextFor,
  documentPreviewOutcomeLabel,
  documentPreviewOutcomeLevel,
  documentProblemFix,
  documentProblemTitle,
  documentStateLabel,
  documentStateLevel,
  documentStatusFor,
  formatAmount,
  formatDay,
  formatNumber,
  notStagedLabel,
  previewOnlyLabel,
} from './labels';
import { useAction } from './useAction';

const PAGE_SIZE = 25;

type KindFilter = '' | ErpDocumentKind;

type ConfirmAction = 'send' | 'skip' | 'confirm_sent' | 'resend';

const confirmCopy: Record<ConfirmAction, (erpName: string, doc: ErpDocument) => { title: string; description: string; label: string; danger?: boolean }> = {
  send: (erpName, doc) => ({
    title: `Send ${documentKindLabel[doc.kind].toLowerCase()} ${doc.document_number} to ${erpName}?`,
    description:
      doc.kind === 'receipt'
        ? `The receipt is sent to ${erpName} now. It is linked only if the invoice is already validated there. This cannot be undone from here.`
        : `The invoice is sent to ${erpName} now. This cannot be undone from here; if it was a mistake, correct it in ${erpName}.`,
    label: 'Send',
  }),
  skip: (erpName, doc) => ({
    title: `Skip ${documentKindLabel[doc.kind].toLowerCase()} ${doc.document_number}?`,
    description: `It is not sent to ${erpName} and it does not come back. If ${erpName} needs it, it has to be entered there outside Commerce Ops.`,
    label: 'Skip',
    danger: true,
  }),
  confirm_sent: (erpName, doc) => ({
    title: `Is ${documentKindLabel[doc.kind].toLowerCase()} ${doc.document_number} in ${erpName}?`,
    description: `Confirm only after finding it in ${erpName}. Nothing is sent.`,
    label: 'Confirm sent',
  }),
  resend: (erpName, doc) => ({
    title: `Send ${documentKindLabel[doc.kind].toLowerCase()} ${doc.document_number} again?`,
    description: `Do this only if it is not in ${erpName}. If it is, ${erpName} gets a second copy.`,
    label: 'Send again',
    danger: true,
  }),
};

/**
 * Invoices (CO-147): the invoices a store issued and the receipts of their
 * payments, built as the ERP's own import files and sent to it automatically,
 * where the operator allows it. There is no file to download and nothing to
 * upload by hand (CO-179). A document that cannot be built says exactly what is
 * missing and who fixes it; nothing is guessed.
 */
export function ErpDocumentsPanel({
  connection,
  canManage,
  onChanged,
}: {
  connection: ErpConnectionView;
  canManage: boolean;
  onChanged: () => void;
}) {
  const settings = connection.settings.documents;
  const erpName = connection.provider_info.display_name;
  const action = useAction();
  const [kind, setKind] = useState<KindFilter>('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ doc: ErpDocument; action: ConfirmAction } | null>(null);
  // The preview of one document: built on request, never sent or stored.
  const [previewed, setPreviewed] = useState<{ reference: string; kind: ErpDocumentKind } | null>(null);
  const [previewRef, setPreviewRef] = useState('');
  const [previewKind, setPreviewKind] = useState<ErpDocumentKind>('invoice');

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setQuery(search.trim());
      setOffset(0);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [search]);

  const states = documentFilterFor(filter).states;
  const documents = useResource(
    () => erpApi.documents(connection.id, { kind: kind || undefined, state: states || undefined, q: query || undefined, limit: PAGE_SIZE, offset }),
    [connection.id, kind, states, query, offset, settings.mode],
    connection.id,
    { key: `erp:${connection.id}:documents:${kind}:${states}:${query}:${offset}:${settings.mode}` },
  );

  const rows = documents.data?.documents ?? [];
  const total = documents.data?.total ?? 0;
  const counts = documents.data?.counts ?? {};
  const countOf = (groupStates: string): number =>
    Object.entries(counts).reduce((sum, [key, value]) => {
      const [rowKind, state = ''] = key.split('/');
      if (kind && rowKind !== kind) return sum;
      return groupStates === '' || groupStates.split(',').includes(state) ? sum + value : sum;
    }, 0);
  const canSend = settings.mode === 'live' && connection.documents_live_allowed;

  const run = (doc: ErpDocument, verb: ErpDocumentAction) =>
    action.run(
      `${verb}:${doc.id}`,
      async () => {
        await erpApi.actOnDocument(connection.id, doc.id, verb);
        documents.reload();
        onChanged();
      },
      documentActionDone[verb],
    );

  // The row keeps three things in view: the one thing to do (send), the
  // details, and a menu for the rest. The menu keeps its slot even when a row
  // has none, so Details and the menu stand in the same place on every row;
  // Send sits to their left and takes room only on the rows that have it.
  const rowActions = (doc: ErpDocument) => {
    const allowed = canManage ? documentActions(doc.state) : [];
    const busy = action.busy !== null;
    const sendable = canSend && allowed.includes('send');
    const name = `${documentKindLabel[doc.kind].toLowerCase()} ${doc.document_number}`;
    const menu: MenuAction[] = [];
    if (canManage) {
      menu.push({ label: 'Preview', onClick: () => setPreviewed({ reference: doc.record_native_id, kind: doc.kind }), disabled: busy });
    }
    for (const decision of ['confirm_sent', 'resend'] as const) {
      if (allowed.includes(decision)) {
        menu.push({ label: documentActionName(decision, erpName), onClick: () => setConfirm({ doc, action: decision }), disabled: busy });
      }
    }
    if (allowed.includes('rebuild')) {
      menu.push({ label: documentActionLabel.rebuild, onClick: () => void run(doc, 'rebuild'), disabled: busy });
    }
    if (allowed.includes('skip')) {
      menu.push({ label: documentActionLabel.skip, onClick: () => setConfirm({ doc, action: 'skip' }), disabled: busy, danger: true });
    }
    return (
      <>
        <span className="rt-actions__lead">
          {sendable ? (
            <button type="button" className="button button--secondary button--small" disabled={busy} onClick={() => setConfirm({ doc, action: 'send' })}>
              {action.busy === `send:${doc.id}` ? 'Sending' : 'Send'}
            </button>
          ) : null}
        </span>
        <button
          type="button"
          className="button button--ghost button--small"
          aria-expanded={open === doc.id}
          aria-controls={open === doc.id ? `erp-doc-detail-${doc.id}` : undefined}
          aria-label={`${open === doc.id ? 'Hide' : 'Show'} details of ${name}`}
          onClick={() => setOpen(open === doc.id ? null : doc.id)}
        >
          {open === doc.id ? 'Hide' : 'Details'}
        </button>
        <span className="rt-actions__menu">
          {menu.length > 0 ? <ActionMenu label={`More actions for ${name}`} items={menu} size="sm" /> : null}
        </span>
      </>
    );
  };

  const columns: Column<ErpDocument>[] = [
    {
      id: 'document',
      header: 'Number',
      card: 'title',
      width: 'fill',
      cell: (doc) => (
        <span className="cell-stack">
          <span className="erp-doc-number">{doc.document_number || '—'}</span>
          <span className="cell-stack__secondary">
            {documentKindLabel[doc.kind]}
            {doc.document_date ? <Fold>{formatDay(doc.document_date)}</Fold> : null}
            <Fold>{formatAmount(doc.gross_total, doc.currency)}</Fold>
          </span>
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      card: 'status',
      width: 'status',
      cell: (doc) => {
        const status = documentStatusFor(doc.state, settings.mode);
        return <LevelBadge level={status.level}>{status.label}</LevelBadge>;
      },
    },
    {
      id: 'date',
      header: 'Date',
      priority: 'secondary',
      width: 12,
      cell: (doc) => (doc.document_date ? <time dateTime={doc.document_date}>{formatDay(doc.document_date)}</time> : '—'),
    },
    {
      id: 'website',
      header: 'Website',
      priority: 'secondary',
      width: 12,
      cell: (doc) => doc.website_code || '—',
    },
    {
      id: 'total',
      header: <span className="rt-num">Total</span>,
      cardLabel: 'Total',
      priority: 'secondary',
      width: 16,
      cell: (doc) => <span className="rt-num tabular-nums erp-doc-nowrap">{formatAmount(doc.gross_total, doc.currency)}</span>,
    },
    {
      id: 'updated',
      header: 'Updated',
      priority: 'secondary',
      width: 'time',
      cell: (doc) => <Timestamp iso={doc.updated_at} />,
    },
  ];

  const kindOptions: { value: KindFilter; label: string }[] = [
    { value: '', label: 'Invoices and receipts' },
    { value: 'invoice', label: 'Invoices' },
    { value: 'receipt', label: 'Receipts' },
  ];

  const confirming = confirm ? confirmCopy[confirm.action](erpName, confirm.doc) : null;
  const mode = settings.mode;

  return (
    <>
      {/* What sending needs comes first while it is not all true; then the documents that need a person; the settings and the preview are actions. */}
      <ErpDocumentReadiness connection={connection} canManage={canManage} onChanged={onChanged} />
      {mode === 'off' && !documents.data?.total ? null : (
        <PanelCard labelledBy="erp-documents-title" testId="erp-documents">
          <SectionHeader id="erp-documents-title" title="Invoices and receipts" />
          <div className="erp-filters erp-filters--documents">
            <Field label="Search">
              <Input
                id="erp-doc-search"
                type="search"
                value={search}
                placeholder="Invoice number…"
                autoComplete="off"
                maxLength={64}
                onChange={(event) => setSearch(event.target.value)}
              />
            </Field>
            <Field label="Show">
              <Select
                id="erp-doc-kind"
                value={kind}
                onChange={(event) => {
                  setKind(event.target.value as KindFilter);
                  setOffset(0);
                }}
              >
                {kindOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <FilterChips
            label="Filter by state"
            value={filter}
            onChange={(value) => {
              setFilter(value);
              setOffset(0);
            }}
            options={documentFilters.map((option) => ({ value: option.value, label: option.label, count: countOf(option.states) }))}
          />
          <ErrorNotice error={documents.error ?? action.error} />
          {action.notice ? (
            <p className="panel__note" role="status">
              {action.notice}
            </p>
          ) : null}
          {documents.loading && !documents.data ? (
            <LoadingRows label="Loading invoices and receipts" rows={4} />
          ) : !documents.data ? null : rows.length === 0 ? (
            <QuietEmpty testId="erp-documents-empty">
              {filter === 'all' && !query && !kind
                ? `No invoices or receipts yet · taking invoices issued from ${settings.since ? formatDay(settings.since) : 'the day this is turned on'}`
                : 'No invoices or receipts match'}
            </QuietEmpty>
          ) : (
            <>
              <ResponsiveTable
                rows={rows}
                rowKey={(doc) => doc.id}
                caption="Invoices and receipts sent to the ERP"
                className="erp-documents-table"
                columns={columns}
                actions={rowActions}
                actionsSize="lg"
                rowProps={(doc) => ({ testId: `erp-document-${doc.id}`, highlighted: doc.state === 'refused' })}
                detail={(doc) =>
                  open === doc.id ? (
                    <DocumentDetail
                      connection={connection}
                      doc={doc}
                      erpName={erpName}
                      busy={action.busy !== null}
                      onDecide={canManage ? (decision) => setConfirm({ doc, action: decision }) : undefined}
                    />
                  ) : null
                }
              />
              <Pager
                first={offset + 1}
                last={offset + rows.length}
                total={total}
                onPrevious={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                onNext={() => setOffset(offset + PAGE_SIZE)}
              />
            </>
          )}
        </PanelCard>
      )}

      <PanelCard labelledBy="erp-documents-settings-title" testId="erp-documents-settings">
        <SectionHeader id="erp-documents-settings-title" title="Invoice settings" />
        <ErpDocumentSettingsForm
          connection={connection}
          canManage={canManage}
          onChanged={onChanged}
          counts={documents.data ? { ready: counts['invoice/ready'] ?? 0, blocked: counts['invoice/blocked'] ?? 0 } : undefined}
        />
      </PanelCard>

      {canManage ? (
        <PanelCard labelledBy="erp-documents-preview-title" testId="erp-documents-preview">
          <SectionHeader id="erp-documents-preview-title" title="Preview" />
          <form
            className="erp-inline-form erp-inline-form--pair"
            onSubmit={(event) => {
              event.preventDefault();
              if (previewRef.trim()) setPreviewed({ reference: previewRef.trim(), kind: previewKind });
            }}
          >
            <Field label="Invoice number">
              <Input
                id="erp-preview-reference"
                value={previewRef}
                autoComplete="off"
                maxLength={64}
                onChange={(event) => setPreviewRef(event.target.value)}
              />
            </Field>
            <Field label="Document">
              <Select id="erp-preview-kind" value={previewKind} onChange={(event) => setPreviewKind(event.target.value as ErpDocumentKind)}>
                <option value="invoice">Invoice</option>
                <option value="receipt">Receipt</option>
              </Select>
            </Field>
            <button type="submit" className="button button--secondary" disabled={!previewRef.trim()} data-testid="erp-preview-run">
              Preview
            </button>
          </form>
        </PanelCard>
      ) : null}

      {confirm && confirming ? (
        <ConfirmDialog
          open
          title={confirming.title}
          description={confirming.description}
          confirmLabel={confirming.label}
          danger={confirming.danger}
          busy={action.busy !== null}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            const pending = confirm;
            setConfirm(null);
            void run(pending.doc, pending.action);
          }}
        />
      ) : null}

      {previewed ? <DocumentPreviewDialog connection={connection} request={previewed} onClose={() => setPreviewed(null)} /> : null}
    </>
  );
}

/**
 * The preview of one document (CO-201): the file it would be sent as, built on
 * request. It is labelled as a preview next to whether a pass has staged the
 * document, because previewed, staged and sent are three different things.
 */
function DocumentPreviewDialog({
  connection,
  request,
  onClose,
}: {
  connection: ErpConnectionView;
  request: { reference: string; kind: ErpDocumentKind };
  onClose: () => void;
}) {
  const [preview, setPreview] = useState<ErpDocumentPreview | null>(null);
  const [error, setError] = useState<Error | null>(null);
  useEffect(() => {
    let current = true;
    setPreview(null);
    setError(null);
    erpApi
      .previewDocument(connection.id, request)
      .then((result) => current && setPreview(result))
      .catch((failure: unknown) => current && setError(failure instanceof Error ? failure : new Error('The preview could not be built.')));
    return () => {
      current = false;
    };
  }, [connection.id, request]);

  return (
    <Dialog
      open
      size="lg"
      title={`Preview · ${documentKindLabel[request.kind].toLowerCase()} ${preview?.document_number || request.reference}`}
      onClose={onClose}
      testId="erp-document-preview"
    >
      <ErrorNotice error={error} />
      {!preview && !error ? <LoadingRows label="Building the preview" rows={3} /> : null}
      {preview ? (
        <div className="erp-doc-detail">
          <p>
            <LevelBadge level="unknown">{previewOnlyLabel}</LevelBadge>{' '}
            <LevelBadge level={documentPreviewOutcomeLevel[preview.outcome]}>{documentPreviewOutcomeLabel[preview.outcome]}</LevelBadge>{' '}
            {preview.staged_state ? (
              <LevelBadge level={documentStateLevel[preview.staged_state]}>{`Staged · ${documentStateLabel[preview.staged_state]}`}</LevelBadge>
            ) : (
              <LevelBadge level="unknown">{notStagedLabel}</LevelBadge>
            )}
            {preview.before_start_day ? <LevelBadge level="warning">Before the start day</LevelBadge> : null}
          </p>
          <dl className="erp-doc-detail__facts" data-testid="erp-preview-facts">
            <div>
              <dt>Company</dt>
              <dd>{preview.company_tax_id}</dd>
            </div>
            <div>
              <dt>Number</dt>
              <dd>{preview.document_number || '—'}</dd>
            </div>
            <div>
              <dt>{`${connection.provider_info.display_name} invoice id`}</dt>
              <dd>{preview.external_id}</dd>
            </div>
            {preview.document_date ? (
              <div>
                <dt>Date</dt>
                <dd>{formatDay(preview.document_date)}</dd>
              </div>
            ) : null}
            {preview.gross_total ? (
              <div>
                <dt>Total</dt>
                <dd className="tabular-nums">{formatAmount(preview.gross_total, preview.currency ?? '')}</dd>
              </div>
            ) : null}
            <div>
              <dt>Sending</dt>
              <dd>
                {documentModeLabel[preview.documents_mode]}
                {preview.documents_mode === 'live' && !preview.live_allowed ? ' · not allowed' : ''}
              </dd>
            </div>
          </dl>
          {preview.waiting_reason ? <p className="erp-doc-detail__reason">{preview.waiting_reason}.</p> : null}
          {preview.skip_reason ? <p className="erp-doc-detail__reason">{preview.skip_reason}.</p> : null}
          {preview.problems.length > 0 ? (
            <ul className="erp-doc-problems" aria-label="What is missing">
              {preview.problems.map((problem, index) => (
                <li key={`${problem.code}:${problem.subject ?? ''}:${index}`}>
                  <strong>{documentProblemTitle[problem.code] ?? problem.code.replace(/_/g, ' ')}</strong>
                  <span>{problem.message}</span>
                  {documentProblemFix[problem.code] ? <span className="erp-doc-problems__fix">{documentProblemFix[problem.code]}</span> : null}
                </li>
              ))}
            </ul>
          ) : null}
          {preview.files.map((file) => (
            <div key={file.name}>
              <p className="erp-doc-detail__label">{file.name}</p>
              <pre className="erp-doc-report" tabIndex={0} role="region" aria-label={`File ${file.name}`}>
                {file.content}
              </pre>
            </div>
          ))}
        </div>
      ) : null}
    </Dialog>
  );
}

/** Why a document stands where it does, what happens next, and who acts. */
function DocumentDetail({
  connection,
  doc,
  erpName,
  busy,
  onDecide,
}: {
  connection: ErpConnectionView;
  doc: ErpDocument;
  erpName: string;
  busy: boolean;
  /** Opens the confirmation of a decision about an unknown outcome; absent where the person cannot decide. */
  onDecide?: (action: 'confirm_sent' | 'resend') => void;
}) {
  // The ERP's own report on a refused file, and the send attempts, are only in the detail.
  const detail = useResource(
    () => erpApi.document(connection.id, doc.id),
    [connection.id, doc.id, doc.updated_at],
    doc.id,
    { key: `erp:${connection.id}:document:${doc.id}:${doc.updated_at}`, enabled: doc.state === 'refused' || doc.attempts > 0 },
  );
  const report = detail.data?.report;
  const history = detail.data?.history ?? [];
  return (
    <div className="erp-doc-detail" id={`erp-doc-detail-${doc.id}`} data-testid="erp-document-detail">
      {documentNextFor(doc.state, connection.settings.documents.mode) ? (
        <p className="erp-doc-detail__next">{documentNextFor(doc.state, connection.settings.documents.mode)}</p>
      ) : null}
      {doc.waiting_reason ? <p className="erp-doc-detail__reason">{doc.waiting_reason}.</p> : null}
      {doc.message && doc.state !== 'blocked' ? <p className="erp-doc-detail__reason">{doc.message}</p> : null}
      {doc.problems.length > 0 ? (
        <ul className="erp-doc-problems" aria-label="What is missing">
          {doc.problems.map((problem, index) => (
            <li key={`${problem.code}:${problem.subject ?? ''}:${index}`}>
              <strong>{documentProblemTitle[problem.code] ?? problem.code.replace(/_/g, ' ')}</strong>
              <span>{problem.message}</span>
              {documentProblemFix[problem.code] ? <span className="erp-doc-problems__fix">{documentProblemFix[problem.code]}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
      {doc.state === 'outcome_unknown' && onDecide ? (
        <div className="erp-doc-detail__decisions">
          {(['confirm_sent', 'resend'] as const).map((decision) => (
            <button key={decision} type="button" className="button button--secondary button--small" disabled={busy} onClick={() => onDecide(decision)}>
              {documentActionName(decision, erpName)}
            </button>
          ))}
        </div>
      ) : null}
      {history.length > 0 ? (
        <div>
          <p className="erp-doc-detail__label">Send history</p>
          <dl className="erp-doc-detail__facts" data-testid="erp-document-history">
            {history.map((attempt) => (
              <div key={attempt.number}>
                <dt>{`#${attempt.number}`}</dt>
                <dd>
                  {documentStateLabel[attempt.state]} · <Timestamp iso={attempt.finished_at ?? attempt.started_at} />
                  {attempt.resolution ? ` · ${attemptResolutionLabel[attempt.resolution]}${attempt.resolved_by ? ` by ${attempt.resolved_by}` : ''}` : ''}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}
      {report ? (
        <div>
          <p className="erp-doc-detail__label">{erpName}’s report</p>
          <pre className="erp-doc-report" tabIndex={0} role="region" aria-label={`${erpName}’s report on this file`}>
            {report}
          </pre>
        </div>
      ) : null}
      <dl className="erp-doc-detail__facts">
        {doc.net_total ? (
          <div>
            <dt>Net</dt>
            <dd className="tabular-nums">{formatAmount(doc.net_total, doc.currency)}</dd>
          </div>
        ) : null}
        {doc.vat_total ? (
          <div>
            <dt>VAT</dt>
            <dd className="tabular-nums">{formatAmount(doc.vat_total, doc.currency)}</dd>
          </div>
        ) : null}
        {doc.attempts > 0 ? (
          <div>
            <dt>Send attempts</dt>
            <dd className="tabular-nums">{formatNumber(doc.attempts)}</dd>
          </div>
        ) : null}
        {doc.sent_at ? (
          <div>
            <dt>Sent</dt>
            <dd>
              <Timestamp iso={doc.sent_at} />
            </dd>
          </div>
        ) : null}
        {doc.decided_by ? (
          <div>
            <dt>Decided by</dt>
            <dd>{doc.decided_by}</dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}
